import React, { useState, useRef, useEffect } from "react";
import { RadarAvatar, RadarCrest, RadarLabelPill, RadarNotesChip } from "./RadarAtoms";
import { radarMetaLine, formatMonthYear, formatDayMonth, notesChipText } from "./radarFormat";

// One radar entry card (design source). Real player data only. Desktop renders the
// ⋯ menu inline with Move/Copy submenus; mobile delegates ⋯ to a bottom sheet the
// page owns (onOpenActions). Clicking the card opens the entry detail.
const EntryCard = ({
  entry,
  mobile = false,
  lists = [],
  selectedList,
  onOpen,
  onMove,
  onCopy,
  onShare,
  onRemove,
  onOpenActions,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState(null); // null | 'move' | 'copy'
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
        setMode(null);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  const closeMenu = () => { setMenuOpen(false); setMode(null); };

  const listIds = entry.list_ids || [];
  const pickLists = mode === "move"
    ? lists.filter((l) => l.id !== selectedList)
    : lists.filter((l) => !listIds.includes(l.id));

  const contract = formatMonthYear(entry.contract_until);
  const notesCount = entry.notes_count || 0;
  const labels = entry.labels || [];

  return (
    <div
      onClick={() => onOpen(entry)}
      className="cursor-pointer relative flex flex-col hover:border-[#BFD3EC]"
      style={{
        background: "#FFFFFF",
        border: "1px solid #E4E9F0",
        borderRadius: 14,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        padding: mobile ? "13px 8px 13px 14px" : 14,
        gap: mobile ? 9 : 10,
      }}
    >
      {/* head */}
      <div className="flex items-start" style={{ gap: 10 }}>
        <RadarAvatar name={entry.name} imageUrl={entry.player_image_url} size={44} fontSize={14} seed={entry.id} />
        <div className="flex-1 min-w-0">
          <div
            className="font-bold text-[#111827] overflow-hidden text-ellipsis whitespace-nowrap"
            style={{ fontSize: mobile ? 14 : 13.5 }}
          >
            {entry.name}
          </div>
          <div className="text-[11.5px] text-[#6B7280]" style={{ marginTop: 1 }}>{radarMetaLine(entry)}</div>
        </div>

        {mobile ? (
          <button
            type="button"
            aria-label="Entry actions"
            onClick={(e) => { e.stopPropagation(); onOpenActions(entry); }}
            className="border-none bg-transparent text-[#9CA3AF] cursor-pointer leading-none flex-shrink-0"
            style={{ width: 44, height: 44, margin: "-8px 0", fontSize: 17, fontWeight: 700, padding: 0 }}
          >
            ⋯
          </button>
        ) : (
          <div className="relative" ref={menuRef} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              aria-label="Entry actions"
              onClick={() => { setMenuOpen((v) => !v); setMode(null); }}
              className="border-none bg-transparent text-[#9CA3AF] hover:text-[#374151] cursor-pointer leading-none"
              style={{ fontSize: 16, fontWeight: 700, padding: "0 3px" }}
            >
              ⋯
            </button>
            {menuOpen && (
              <div
                className="absolute z-30"
                style={{ right: 0, top: 26, minWidth: 190, background: "#FFFFFF", border: "1px solid #E4E9F0", borderRadius: 9, boxShadow: "0 8px 24px rgba(17,24,39,0.12)", padding: 5 }}
              >
                {!mode && (
                  <>
                    <MenuItem onClick={() => setMode("move")}>Move to list…</MenuItem>
                    <MenuItem onClick={() => setMode("copy")}>Copy to list…</MenuItem>
                    <MenuItem onClick={() => { closeMenu(); onShare(entry); }}>Share to chat</MenuItem>
                    <div style={{ height: 1, background: "#EEF1F5", margin: "4px 2px" }} />
                    <MenuItem danger onClick={() => { closeMenu(); onRemove(entry); }}>Remove from radar</MenuItem>
                  </>
                )}
                {mode && (
                  <>
                    <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#9CA3AF]" style={{ padding: "6px 10px 4px" }}>
                      {mode === "move" ? "Move to…" : "Copy to…"}
                    </div>
                    {pickLists.map((l) => (
                      <MenuItem
                        key={l.id}
                        onClick={() => { closeMenu(); (mode === "move" ? onMove : onCopy)(entry, l); }}
                      >
                        {l.name}
                      </MenuItem>
                    ))}
                    {pickLists.length === 0 && (
                      <div className="text-[12px] text-[#9CA3AF]" style={{ padding: "7px 10px" }}>No other lists yet</div>
                    )}
                    <div style={{ height: 1, background: "#EEF1F5", margin: "4px 2px" }} />
                    <MenuItem muted onClick={() => setMode(null)}>‹ Back</MenuItem>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* club + contract */}
      <div className="flex flex-col" style={{ gap: 3, paddingRight: mobile ? 6 : 0 }}>
        <div className="flex items-center" style={{ gap: 6 }}>
          <RadarCrest name={entry.club_name} logoUrl={entry.club_logo_url} size={16} seed={entry.id} />
          <div className="text-[12.5px] text-[#374151] font-semibold truncate">{entry.club_name}</div>
        </div>
        {contract && <div className="text-[11.5px] text-[#9CA3AF]">Contract until {contract}</div>}
      </div>

      {/* labels + notes + added */}
      <div className="flex items-center flex-wrap" style={{ gap: 5, paddingRight: mobile ? 6 : 0 }}>
        {labels.map((lb) => (
          <RadarLabelPill key={lb.id} label={lb} />
        ))}
        {notesCount > 0 && <RadarNotesChip>{notesChipText(notesCount)}</RadarNotesChip>}
        <span className="text-[11px] text-[#9CA3AF]" style={{ marginLeft: "auto" }}>Added {formatDayMonth(entry.added_at)}</span>
      </div>
    </div>
  );
};

const MenuItem = ({ children, onClick, danger, muted }) => (
  <button
    type="button"
    onClick={onClick}
    className={`block w-full text-left bg-transparent border-none rounded-md text-[12.5px] font-medium cursor-pointer ${
      danger ? "text-[#DC2626] hover:bg-[#FEF2F2]" : muted ? "text-[#9CA3AF] hover:bg-[#F3F6FA]" : "text-[#374151] hover:bg-[#F3F6FA]"
    }`}
    style={{ padding: "7px 10px" }}
  >
    {children}
  </button>
);

export default EntryCard;
